import { buildApiUrl } from './apiBase';

export class ApiError extends Error {
  public code: string;
  public status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

async function fetchApi(endpoint: string, options: RequestInit = {}) {
  const url = buildApiUrl(endpoint);
  if (!url) {
    throw new ApiError('API Base URL is not configured', 'NO_BASE_URL', 500);
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const message = data?.error?.message || data?.message || response.statusText || 'Unknown Error';
    const code = data?.error?.code || data?.code || 'API_ERROR';
    throw new ApiError(message, code, response.status);
  }

  return data;
}

export async function authWithGoogleCredential(credential: string) {
  try {
    // Never expose credential to anything other than our worker API
    // Use direct same-origin fetch to bypass any generic API base or legacy worker
    const response = await fetch('/api/auth/google', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ credential }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const message = data?.error?.message || data?.message || response.statusText || 'Unknown Error';
      const code = data?.error?.code || data?.code || 'API_ERROR';

      if (code === 'CONFIG_MISSING' || code === 'SESSION_SECRET_MISSING') {
        return { ok: false, code: 'config_missing', message };
      }

      throw new ApiError(message, code, response.status);
    }

    return data;
  } catch (err: any) {
    if (err instanceof ApiError) {
      throw err;
    }
    // Only true network errors or TypeError from fetch will reach here
    if (err?.message?.includes('Failed to fetch') || err?.name === 'TypeError') {
      console.warn('Auth API unavailable, returning safe fallback.');
      return { ok: false, code: 'api_base_unavailable' };
    }
    throw err;
  }
}

export async function getSession(sessionToken: string) {
  return fetchApi('/api/session', {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });
}

export async function createTenant(sessionToken: string, payload: any) {
  console.info('[DEBUG] SESSION_TOKEN_PRESENT:', !!sessionToken);
  console.info('[DEBUG] AUTH_HEADER_WOULD_BE_ATTACHED:', !!sessionToken);
  console.info('[DEBUG] CREATE_TENANT_USES_FETCH_API:', true);

  if (!sessionToken) {
    throw new ApiError('session expired / please login again', 'UNAUTHORIZED', 401);
  }

  return fetchApi('/api/tenants', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function updateTenantSettings(sessionToken: string, tenantId: string, payload: any) {
  return fetchApi(`/api/tenants/${tenantId}/settings`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(payload),
  });
}

export async function getTenantProfile(sessionToken: string, tenantId: string) {
  try {
    return await fetchApi(`/api/tenants/${tenantId}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      }
    });
  } catch (err: any) {
    if (err?.code === 'NO_BASE_URL' || err?.message?.includes('Failed to fetch') || err?.name === 'TypeError') {
      console.warn('Profile API unavailable, returning safe fallback.');
      return { ok: false, __profile_unavailable: true };
    }
    throw err;
  }
}

export async function getAdminV2LoginUrl(sessionToken: string, tenantId: string) {
  return fetchApi(`/api/tenants/${tenantId}/admin-login-url`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    }
  });
}

export async function getTenantV2OwnerPreviewUrl(sessionToken: string, tenantId: string) {
  return fetchApi(`/api/tenants/${tenantId}/tenant-preview-url`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    }
  });
}

export async function completeTenantOnboarding(sessionToken: string, tenantId: string) {
  return fetchApi(`/api/tenants/${tenantId}/complete`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    }
  });
}

export async function getSubscriptionPlans(sessionToken: string) {
  return fetchApi('/api/subscription-plans', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    }
  });
}

export async function createPaymentIntent(sessionToken: string, tenantId: string, planId: string) {
  return fetchApi('/api/payment-intents', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ tenantId, planId })
  });
}

export async function submitMockSlip(sessionToken: string, subscriptionId: string, transactionRef: string, amount: number, note: string = '') {
  return fetchApi('/api/payment-slips/mock', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ subscriptionId, transactionRef, amount, note })
  });
}

export async function uploadTenantLogo(sessionToken: string, tenantId: string, file: File): Promise<{ ok: boolean; logoUrl?: string; objectKey?: string }> {
  const formData = new FormData();
  formData.append('tenantId', tenantId);
  formData.append('logo', file);

  const url = buildApiUrl(`/api/assets/logo`);
  if (!url) {
    throw {
      name: 'ApiError',
      message: 'API Base URL is not configured',
      status: 500,
      code: 'NO_BASE_URL',
      endpoint: '/api/assets/logo'
    };
  }

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: formData,
    });
  } catch (err: any) {
    throw {
      name: err.name || 'TypeError',
      message: err.message || 'Failed to fetch',
      status: null,
      code: 'NETWORK_OR_CLIENT_BLOCKED',
      endpoint: '/api/assets/logo'
    };
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw {
      name: 'ApiError',
      message: data.error?.message || 'Failed to upload logo',
      status: response.status,
      code: data.error?.code || 'HTTP_ERROR',
      endpoint: '/api/assets/logo'
    };
  }

  return response.json();
}

export async function verifyPaymentSlip(
  sessionToken: string,
  subscriptionId: string,
  file: File,
  qrData: string,
  amount: number
): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('qrData', qrData);
  formData.append('amount', amount.toString());

  const url = buildApiUrl(`/api/payment-slips/${subscriptionId}/verify`);
  if (!url) {
    throw new ApiError('API Base URL is not configured', 'NO_BASE_URL', 500);
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new ApiError(data.error?.message || 'Failed to upload slip', data.error?.code || 'UPLOAD_ERROR', response.status);
    if (data.error?.retryAfterSeconds) {
      (err as any).retryAfterSeconds = data.error.retryAfterSeconds;
    }
    throw err;
  }

  return response.json();
}

export async function getIntegrationPayload(sessionToken: string, tenantId: string) {
  return fetchApi(`/api/tenants/${tenantId}/integration-payload`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    }
  });
}

export async function provisionToApartment(sessionToken: string, tenantId: string, adminBaseUrl?: string, testRunId?: string) {
  return fetchApi(`/api/tenants/${tenantId}/provision-to-apartment`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify({ adminBaseUrl, testRunId })
  });
}
